/**
 * Supported UI locales for the web app.
 *
 * Keep codes BCP-47 style (`pt`, `ar`) so `Intl` APIs, Capacitor, and any
 * future backend `Accept-Language` negotiation agree on the same values.
 * `dir` drives the `document.dir` switch in `i18n.ts` (Arabic is RTL).
 */
export interface AppLanguage {
  /** i18next language code, also the locale JSON filename. */
  code: string;
  /** Label shown in its own script inside the language picker. */
  label: string;
  /** English label, useful for support/debugging surfaces. */
  englishLabel: string;
  dir: 'ltr' | 'rtl';
}

export const APP_LANGUAGES: AppLanguage[] = [
  { code: 'en', label: 'English', englishLabel: 'English', dir: 'ltr' },
  { code: 'ta', label: 'தமிழ்', englishLabel: 'Tamil', dir: 'ltr' },
  { code: 'hi', label: 'हिन्दी', englishLabel: 'Hindi', dir: 'ltr' },
  { code: 'te', label: 'తెలుగు', englishLabel: 'Telugu', dir: 'ltr' },
  { code: 'ml', label: 'മലയാളം', englishLabel: 'Malayalam', dir: 'ltr' },
  { code: 'kn', label: 'ಕನ್ನಡ', englishLabel: 'Kannada', dir: 'ltr' },
  { code: 'es', label: 'Español', englishLabel: 'Spanish', dir: 'ltr' },
  { code: 'fr', label: 'Français', englishLabel: 'French', dir: 'ltr' },
  { code: 'pt', label: 'Português', englishLabel: 'Portuguese', dir: 'ltr' },
  { code: 'ar', label: 'العربية', englishLabel: 'Arabic', dir: 'rtl' },
];

export const APP_LANGUAGE_CODES = APP_LANGUAGES.map((l) => l.code);

export const DEFAULT_LANGUAGE = 'en';

export const LANGUAGE_STORAGE_KEY = 'ai-media.language';

/** Normalises a stored/navigator value to a supported code. */
export function resolveLanguage(candidate: string | null | undefined): string {
  if (!candidate) return DEFAULT_LANGUAGE;
  const base = candidate.toLowerCase().split(/[-_]/)[0];
  return APP_LANGUAGE_CODES.includes(base) ? base : DEFAULT_LANGUAGE;
}

export function languageDir(code: string): 'ltr' | 'rtl' {
  return APP_LANGUAGES.find((l) => l.code === code)?.dir ?? 'ltr';
}
